import { useCallback, useEffect, useState } from "react"

import type { Outcome, SigningState, SigningTransport } from "./types.ts"
import { useNewestOnly } from "./use-newest-only.ts"

export interface SigningLoad {
  state: SigningState | null
  bytes: Uint8Array | null
  failed: Outcome<unknown> | null
  /** `null`: a newer read overtook this one. */
  reload(): Promise<Outcome<SigningState> | null>
  retry(): void
}

export const unanswered = <T>(): Outcome<T> => ({
  ok: false,
  status: 0,
  data: null,
  reason: null,
  detail: null
})

// 403 too: a countersign envelope the caller may no longer see.
export const isGone = (outcome: Outcome<unknown>) =>
  [403, 404, 410].includes(outcome.status)

export const worthRetrying = (outcome: Outcome<unknown>) =>
  outcome.status === 0 || outcome.status >= 500

export const ask = async <T>(
  request: () => Promise<Outcome<T>>
): Promise<Outcome<T>> => {
  try {
    return await request()
  } catch {
    return unanswered<T>()
  }
}

export const useSigning = (transport: SigningTransport): SigningLoad => {
  const [state, setState] = useState<SigningState | null>(null)
  const [bytes, setBytes] = useState<Uint8Array | null>(null)
  const [failed, setFailed] = useState<Outcome<unknown> | null>(null)
  const newest = useNewestOnly()

  const load = useCallback(() => {
    const current = newest.claim()
    setState(null)
    setBytes(null)
    setFailed(null)
    void Promise.all([
      ask(() => transport.getState()),
      ask(() => transport.getDocument())
    ]).then(([stateOutcome, documentOutcome]) => {
      if (!current()) return
      if (!stateOutcome.ok || !stateOutcome.data) return setFailed(stateOutcome)
      if (!documentOutcome.ok || !documentOutcome.data)
        return setFailed(documentOutcome)
      setState(stateOutcome.data)
      setBytes(documentOutcome.data)
    })
  }, [transport, newest])

  useEffect(load, [load])

  const reload = useCallback(async () => {
    const current = newest.claim()
    const outcome = await ask(() => transport.getState())
    if (!current()) return null
    if (outcome.ok && outcome.data) setState(outcome.data)
    else if (isGone(outcome)) setFailed(outcome)
    return outcome
  }, [transport, newest])

  return { state, bytes, failed, reload, retry: load }
}
